// FilletPreview — drag-стрелка для операции «Скругление» (fillet/chamfer).
//
// Идея: когда юзер выбрал ребро и активна операция fillet/chamfer, рядом с
// серединой ребра рисуется 3D-стрелка (цилиндр + конус). Drag по экрану
// меняет radius в pendingFeature → debounced live-update в Properties
// пересчитывает тело через replicad.fillet().
//
// Направление стрелки: радиально от центра масс тела (приблизительная
// биссектриса между смежными гранями для прямоугольных рёбер). Для куба
// стрелка идёт от середины ребра под ~45° к двум прилегающим граням.

import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import useCadStore from '../store/useCadStore';


// Длина стрелки в мире (мм). Берём не привязанной к радиусу — всегда видна.
const ARROW_LENGTH_BASE = 30;
const STEM_RADIUS = 0.5;
const HEAD_RADIUS = 2.0;
const HEAD_LENGTH = 4.5;


// Вычисляем середину ребра + направление наружу из центра тела.
// Возвращает { mid: Vector3, dir: Vector3 } или null если данных недостаточно.
function computeArrowOrigin(bodyMesh, edgeId) {
    if (!bodyMesh || !bodyMesh.edges || !bodyMesh.edgeIds) return null;
    const idx = bodyMesh.edgeIds.indexOf(edgeId);
    if (idx < 0) return null;
    const startVert = bodyMesh.edgeOffsets[idx];
    const endVert   = bodyMesh.edgeOffsets[idx + 1];
    if (endVert <= startVert) return null;
    // Средняя точка ребра — берём середину массива
    const numVerts = endVert - startVert;
    const midVertIdx = startVert + Math.floor(numVerts / 2);
    const mid = new THREE.Vector3(
        bodyMesh.edges[midVertIdx * 3],
        bodyMesh.edges[midVertIdx * 3 + 1],
        bodyMesh.edges[midVertIdx * 3 + 2]
    );
    // Направление наружу: от центра bbox тела к midpoint ребра.
    const bbox = computeBodyBbox(bodyMesh);
    if (!bbox) return null;
    const center = new THREE.Vector3(
        (bbox.min.x + bbox.max.x) / 2,
        (bbox.min.y + bbox.max.y) / 2,
        (bbox.min.z + bbox.max.z) / 2
    );
    // Стрелка тянется ВНУТРЬ тела — это направление куда «съедается» материал
    // при скруглении. Drag в эту сторону = увеличение радиуса.
    const dir = new THREE.Vector3().subVectors(center, mid).normalize();
    return { mid, dir };
}


// Bbox тела по positions массиву.
function computeBodyBbox(mesh) {
    const p = mesh.positions;
    if (!p || p.length < 3) return null;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < p.length; i += 3) {
        if (p[i] < minX) minX = p[i]; if (p[i] > maxX) maxX = p[i];
        if (p[i + 1] < minY) minY = p[i + 1]; if (p[i + 1] > maxY) maxY = p[i + 1];
        if (p[i + 2] < minZ) minZ = p[i + 2]; if (p[i + 2] > maxZ) maxZ = p[i + 2];
    }
    return {
        min: new THREE.Vector3(minX, minY, minZ),
        max: new THREE.Vector3(maxX, maxY, maxZ),
    };
}


// Кватернион поворота локальной +Z в направление dir.
function quaternionFromZTo(dir) {
    const z = new THREE.Vector3(0, 0, 1);
    const q = new THREE.Quaternion().setFromUnitVectors(z, dir.clone().normalize());
    return q;
}


const FilletArrow = ({ origin, dir, radius, onChange }) => {
    const { camera, gl, controls } = useThree();
    const groupRef = useRef();
    const draggingRef = useRef(false);
    const startRef = useRef({});
    const wasEnabledRef = useRef(true);

    const arrowLen = ARROW_LENGTH_BASE + Math.min(radius, 50) * 0.3;
    const stemLen = Math.max(arrowLen - HEAD_LENGTH, 0.1);
    const stemMidZ = stemLen / 2;
    const headBaseZ = stemLen;
    const headTipZ = arrowLen;

    const stemGeom = useMemo(() => {
        const g = new THREE.CylinderGeometry(STEM_RADIUS, STEM_RADIUS, stemLen, 16);
        g.rotateX(Math.PI / 2);
        return g;
    }, [stemLen]);
    const headGeom = useMemo(() => {
        const g = new THREE.ConeGeometry(HEAD_RADIUS, HEAD_LENGTH, 24);
        g.rotateX(Math.PI / 2);
        return g;
    }, []);

    const groupQuaternion = useMemo(() => quaternionFromZTo(dir), [dir]);

    useEffect(() => {
        const dom = gl.domElement;
        const onPointerMove = (e) => {
            if (!draggingRef.current) return;
            const ax = startRef.current.axisScreen;
            if (!ax) return;
            const dx = e.clientX - startRef.current.clientX;
            const dy = e.clientY - startRef.current.clientY;
            const proj = dx * ax.x + dy * ax.y;
            const mmPerPx = startRef.current.mmPerPx;
            const newR = startRef.current.radius + proj * mmPerPx;
            const safe = Math.max(0.1, newR);
            const rounded = Math.round(safe * 10) / 10;
            onChange(rounded);
            // Обновляем inline-input
            if (typeof window.__kubikonCadFilletDragMove === 'function') {
                try {
                    window.__kubikonCadFilletDragMove({
                        value: rounded,
                        clientX: e.clientX,
                        clientY: e.clientY,
                    });
                } catch (_) {}
            }
        };
        const onPointerUp = () => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            if (controls && 'enabled' in controls) {
                controls.enabled = wasEnabledRef.current;
            }
            try { dom.releasePointerCapture(startRef.current.pointerId); } catch (_) {}
            if (typeof window.__kubikonCadFilletDragEnd === 'function') {
                try { window.__kubikonCadFilletDragEnd(); } catch (_) {}
            }
        };
        dom.addEventListener('pointermove', onPointerMove);
        dom.addEventListener('pointerup', onPointerUp);
        dom.addEventListener('pointercancel', onPointerUp);
        return () => {
            dom.removeEventListener('pointermove', onPointerMove);
            dom.removeEventListener('pointerup', onPointerUp);
            dom.removeEventListener('pointercancel', onPointerUp);
        };
    }, [gl, onChange, controls]);

    const onArrowDown = useCallback((e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        if (!groupRef.current) return;
        const dom = gl.domElement;
        const rect = dom.getBoundingClientRect();
        const project = (v) => {
            const w = new THREE.Vector3(v[0], v[1], v[2]);
            groupRef.current.updateMatrixWorld(true);
            w.applyMatrix4(groupRef.current.matrixWorld);
            w.project(camera);
            return {
                x: rect.left + (w.x + 1) / 2 * rect.width,
                y: rect.top + (1 - w.y) / 2 * rect.height,
            };
        };
        const O = project([0, 0, 0]);
        const Z = project([0, 0, 1]);
        const sx = Z.x - O.x, sy = Z.y - O.y;
        const slen = Math.sqrt(sx * sx + sy * sy) || 1;
        const axisScreen = { x: sx / slen, y: sy / slen };
        const mmPerPx = 1 / slen;

        if (controls && 'enabled' in controls) {
            wasEnabledRef.current = controls.enabled;
            controls.enabled = false;
        }
        draggingRef.current = true;
        startRef.current = {
            clientX: e.clientX,
            clientY: e.clientY,
            radius,
            axisScreen,
            mmPerPx,
            pointerId: e.pointerId,
        };
        try { dom.setPointerCapture(e.pointerId); } catch (_) {}
        // Открываем inline-input возле курсора
        if (typeof window.__kubikonCadFilletDragStart === 'function') {
            try {
                window.__kubikonCadFilletDragStart({
                    value: radius,
                    clientX: e.clientX,
                    clientY: e.clientY,
                });
            } catch (_) {}
        }
    }, [camera, gl, radius, controls]);

    return (
        <group ref={groupRef} position={origin} quaternion={groupQuaternion}>
            <mesh geometry={stemGeom} position={[0, 0, stemMidZ]} onPointerDown={onArrowDown}>
                <meshBasicMaterial color="#6dc36d" depthTest={false}/>
            </mesh>
            <mesh
                geometry={headGeom}
                position={[0, 0, (headBaseZ + headTipZ) / 2]}
                onPointerDown={onArrowDown}
            >
                <meshBasicMaterial color="#6dc36d" depthTest={false}/>
            </mesh>
        </group>
    );
};


const FilletPreview = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const bodyMeshes = useCadStore((s) => s.bodyMeshes);

    const isFilletKind = pendingFeature?.kind === 'fillet' || pendingFeature?.kind === 'chamfer';
    const firstEdgeRef = isFilletKind ? pendingFeature?.edgeRefs?.[0] : null;

    // Замороженный arrowSpec — ставится при ПЕРВОМ выборе ребра и не
    // пересчитывается пока edgeRef тот же. Иначе при live-preview новый mesh
    // не содержит исходного edgeId (он скруглён) — computeArrowOrigin
    // возвращает null и стрелка исчезает посреди drag'а.
    const [frozenSpec, setFrozenSpec] = useState(null);
    const frozenForRefRef = useRef(null);

    useEffect(() => {
        if (!firstEdgeRef) {
            setFrozenSpec(null);
            frozenForRefRef.current = null;
            return;
        }
        // Если frozenSpec уже установлен для этого ref — ничего не делаем.
        // При live-preview bodyMeshes обновляется, но edgeRef тот же, значит
        // позиция стрелки остаётся на своём месте.
        if (frozenForRefRef.current === firstEdgeRef) return;

        const [bodyId, edgeIdStr] = firstEdgeRef.split('/');
        const edgeId = parseInt(edgeIdStr, 10);
        const mesh = bodyMeshes.get(bodyId);
        if (!mesh) return;
        const spec = computeArrowOrigin(mesh, edgeId);
        if (spec) {
            setFrozenSpec(spec);
            frozenForRefRef.current = firstEdgeRef;
        }
    }, [firstEdgeRef, bodyMeshes]);

    const onArrowChange = (newR) => {
        const tick = (pendingFeature.previewTick || 0) + 1;
        updatePendingFeature({ radius: newR, previewTick: tick });
    };

    if (!isFilletKind) return null;
    if (!frozenSpec) return null;

    return (
        <FilletArrow
            // key привязан к ref ребра — при смене ребра компонент пересоздаётся.
            // При live-preview ref не меняется → компонент сохраняется → drag-state
            // (внутри useRef в FilletArrow) не теряется.
            key={firstEdgeRef}
            origin={[frozenSpec.mid.x, frozenSpec.mid.y, frozenSpec.mid.z]}
            dir={frozenSpec.dir}
            radius={pendingFeature.radius || 0}
            onChange={onArrowChange}
        />
    );
};

export default FilletPreview;
